import React, { lazy, Suspense } from 'react';

import { Outlet, Route, Routes } from 'react-router-dom';

import { YatPageVersion } from '@app/client/common/structs/yatPage/yat-page';
import { useProfilePageInit } from '@app/pages/ProfilePage/hooks';
import { useCheckPublicRedirects } from '@app/routes/hooks';

import { useSentryRoutes } from '../hooks/useSentryRoutes';

const PageNotFound = lazy(() => import('@app/pages/PageNotFound').then((module) => ({ default: module.PageNotFound })));

const RedirectWrapper = lazy(() =>
    import('@app/routes/components').then((module) => ({ default: module.RedirectWrapper })),
);

const YatPage = lazy(() => import('@app/pages/YatPage'));
const NFTShareablePage = lazy(() => import('@app/pages/NFTShareable'));

const ProfilePage = lazy(() => import('@app/pages/ProfilePage').then((module) => ({ default: module.ProfilePage })));
const NFTSection = lazy(() => import('@app/pages/ProfilePage/sections/NFTSection'));
const LinksSection = lazy(() => import('@app/pages/ProfilePage/sections/LinksSection'));
const YatsSection = lazy(() => import('@app/pages/ProfilePage/sections/YatsSection'));
const WishlistsSection = lazy(() =>
    import('@app/pages/ProfilePage/sections/WishlistsSection').then((module) => ({
        default: module.WishlistsSection,
    })),
);
const HistorySection = lazy(() => import('@app/pages/ProfilePage/sections/HistorySection'));
const OffersSection = lazy(() =>
    import('@app/pages/ProfilePage/sections/OffersSection').then((module) => ({ default: module.OffersSection })),
);

function ProfileSectionRoutes() {
    const sections = useSentryRoutes([
        { index: true, element: <NFTSection /> },
        { path: 'links', element: <LinksSection /> },
        { path: 'yats', element: <YatsSection /> },
        { path: 'wishlists', element: <WishlistsSection /> },
        { path: 'history', element: <HistorySection /> },
        { path: 'offers', element: <OffersSection /> },
        { path: '*', element: <PageNotFound hideGlobalNav={true} /> },
    ]);
    return <Suspense>{sections}</Suspense>;
}
function SuspenseOutlet() {
    return (
        <>
            <Outlet />
            <Suspense fallback={<div />} />
        </>
    );
}
const legacyPageRoutes = ['edit', 'editlinks', 'go'];
export const EmojiIDRoutes = () => {
    const { redirectTo } = useCheckPublicRedirects();
    const { isNotFound, pageVersion, eid, isValidEmojiId, isLoading } = useProfilePageInit();

    const isV1 = pageVersion === YatPageVersion.V1;

    return (
        <Routes>
            <Route element={<SuspenseOutlet />}>
                <Route path="nft-shareable" element={<NFTShareablePage />} />
                <Route element={<RedirectWrapper redirectTo={redirectTo} />}>
                    {isV1 && legacyPageRoutes.map((path) => <Route key={path} path={path} element={<YatPage />} />)}
                </Route>

                <Route
                    element={
                        <ProfilePage
                            isNotFound={isNotFound}
                            pageVersion={pageVersion}
                            eid={eid}
                            isValidEmojiId={isValidEmojiId}
                            isLoading={isLoading}
                        />
                    }
                >
                    <Route path="*" element={<ProfileSectionRoutes />} />
                </Route>
            </Route>
            <Route path="*" element={<PageNotFound />} />
        </Routes>
    );
};
